import * as logger from "../utils/logger";
import { HOS_STANDARD_STATUSES } from "../model/DutyStatusLog";
import type DutyStatusLog from "../model/DutyStatusLog";
import { MINUTES } from "../constants";

// Detect 10 standard-status DSL within any 10-min sliding window to trigger a dump
export const WINDOW_MS = 10 * MINUTES;
export const THRESHOLD = 10;

export interface SpamCtx {
  source: string;
  syncLater?: boolean;
}

interface Entry {
  t: number;
  status: string;
}

function interLogGaps(entries: Entry[]): string {
  if (entries.length < 2) {
    return "n/a";
  }
  const gaps: number[] = [];
  for (let i = 1; i < entries.length; i++) {
    gaps.push(Math.round((entries[i].t - entries[i - 1].t) / 1000));
  }
  gaps.sort((a, b) => a - b);
  const min = gaps[0];
  const max = gaps[gaps.length - 1];
  const med = gaps[Math.floor(gaps.length / 2)];
  return `${ min }/${ med }/${ max }`;
}

export class DslSpamDetector {
  private readonly windows = new Map<string, Entry[]>();
  private readonly armed = new Map<string, boolean>();

  constructor(private readonly now: () => number = () => Date.now()) {}

  record(log: DutyStatusLog, ctx: SpamCtx): void {
    try {
      // Only ON/OFF/D/SB -- the four statuses seen in all confirmed spam incidents.
      // PC/YM/WT are excluded intentionally; they are uncommon and not in incident data.
      if (HOS_STANDARD_STATUSES.indexOf(log.status) === -1) {
        return;
      }

      const driverId = log.getDriverId() || "none";
      const t = this.now();
      const win = this.prune(driverId, t);

      win.push({ t, status: log.status });

      if (win.length >= THRESHOLD) {
        if (this.armed.get(driverId) !== false) {
          this.emit(driverId, win, log, ctx);
          this.armed.set(driverId, false);
        }
      } else {
        if (this.armed.get(driverId) === false) {
          this.armed.set(driverId, true);
          this.windows.set(driverId, [{ t, status: log.status }]);
        }
      }
    } catch {
      // Detector must never interfere with DSL creation.
    }
  }

  private prune(driverId: string, t: number): Entry[] {
    const cutoff = t - WINDOW_MS;
    const win = (this.windows.get(driverId) || []).filter(e => e.t > cutoff);
    this.windows.set(driverId, win);
    return win;
  }

  private emit(
    driverId: string,
    win: Entry[],
    log: DutyStatusLog,
    ctx: SpamCtx
  ): void {
    // min/median/max seconds between consecutive logs; tight gaps (0-1s) = programmatic
    const gaps = interLogGaps(win);
    // Last 20 entries as status@HH:MM:SS for visual pattern inspection
    const buffer = win
      .slice(-20)
      .map(e => `${ e.status }@${ new Date(e.t).toISOString().substring(11, 19) }`)
      .join(",");
    logger.log(
      logger.Source.USER_EVENT,
      `[DSL:SPAM] driver=${ driverId } ` +
        `gaps(min/med/max)=${ gaps } buffer=[${ buffer }] ` +
        `trigger=${ log.status }/${ log.origin }@${ log.dateTime } id=${ log.id } ` +
        `syncLater=${ ctx.syncLater } source=${ ctx.source } ` +
        `stack=${ new Error().stack || "" }`
    );
  }
}

export const dslSpamDetector = new DslSpamDetector();
